import { useContext, useEffect, useMemo } from 'react';

import styled from '@emotion/styled';
import { AppSettingsContext, ROUTES } from 'core';
import { useLazyGetUnreadMessagesCountQuery } from 'store/inbox/inboxApi';
import { useLazyGetSessionsAvailableInPrsQuery } from 'store/post-results-services/postResultsServicesApi';
import { FaExclamationCircle } from 'react-icons/fa';

import { Dropdown, Link, PrimaryNav } from '@britishcouncil/react-solas';

const S = {
  NavWrapper: styled.div`
    &.inbox-has-messages a[href='/inbox'] {
      font-weight: 700;
    }
  `,
};

type MainNavbarProps = {
  isLoggedIn?: boolean;
};

const MainNavbar = ({ isLoggedIn }: MainNavbarProps) => {
  const [unreadMessageQuery, { isFetching: unreadMessagesFetching, data: unreadMessagesCount }] =
    useLazyGetUnreadMessagesCountQuery();
  const [sessionInPrsQuery, { data: sessionsInPrs }] = useLazyGetSessionsAvailableInPrsQuery();

  const appSettings = useContext(AppSettingsContext);

  useEffect(() => {
    if (isLoggedIn) {
      unreadMessageQuery();
      if (appSettings.prsFeatureEnabled) sessionInPrsQuery();
    }
  }, [isLoggedIn, unreadMessageQuery, sessionInPrsQuery, appSettings.prsFeatureEnabled]);

  const inboxName = useMemo(() => {
    if (unreadMessagesFetching) {
      return 'Inbox';
    }

    return `Inbox (${unreadMessagesCount})`;
  }, [unreadMessagesFetching, unreadMessagesCount]);

  return (
    <S.NavWrapper className={unreadMessagesCount && unreadMessagesCount > 0 ? 'inbox-has-messages' : ''}>
      <PrimaryNav>
        <Link to={ROUTES.COUNTRY_SELECTOR} name={'Change country/territory'} />
        {isLoggedIn && (
          <>
            <Dropdown to={ROUTES.MY_REGISTRATIONS} name="My registrations">
              <Link to={ROUTES.MY_REGISTRATIONS_PAYMENTS} name={'Payments'} />
              <Link to={ROUTES.MY_REGISTRATIONS_SUMMARY} name={'Summary'} />
            </Dropdown>
            <Link to={ROUTES.CHANGE_PASSWORD} name={'Change password'} />
            <Link to={ROUTES.MY_PROFILE} name={'My profile'} />
            <Link to={ROUTES.INBOX} name={inboxName} />
            {appSettings.prsFeatureEnabled && (
              <Link
                to={ROUTES.POST_RESULTS_SERVICES}
                name={'Post results services'}
                icon={
                  sessionsInPrs && (sessionsInPrs.isActivePrsBeforeDeadline || sessionsInPrs.names.length > 0) ? (
                    <FaExclamationCircle color="red" />
                  ) : undefined
                }
                iconPosition="right"
              />
            )}
          </>
        )}
      </PrimaryNav>
    </S.NavWrapper>
  );
};

export default MainNavbar;
